import { computed, inject, Injectable, INJECTOR, signal } from '@angular/core';
import { PalletCorrectionRepository } from '../repositories/pallet-correction.repository';
import { ArtikelMenge, Lagerplatz, LagerplatzInhalt } from '@softapps/lager/core';
import { handleRequestError } from '@softline/application';

@Injectable()
export class PalletCorrectionService {
  readonly #repository = inject(PalletCorrectionRepository);
  readonly #injector = inject(INJECTOR);

  readonly corrections = signal(
    new Map<number, { lagerplatz: Lagerplatz; artikelMenge: ArtikelMenge }>()
  );

  readonly active = signal<LagerplatzInhalt | undefined>(undefined);
  readonly loading = signal<boolean>(false);

  readonly hasChanges = computed(() => this.corrections().size > 0);

  async find(value: string | undefined): Promise<LagerplatzInhalt | null> {
    if (!value) return null;

    try {
      this.loading.set(true);
      const result = await this.#repository.find(value);
      this.active.set(result);
      this.loading.set(false);
      return result;
    } catch (e) {
      this.loading.set(false);
      handleRequestError(e, this.#injector, { show: true });
      return null;
    }
  }

  commitUpdate(
    item: ArtikelMenge,
    values: { storageLocation: Lagerplatz; quantity: number }
  ): void {
    const updatedMap = this.corrections().set(item.artikel.id, {
      lagerplatz: values.storageLocation,
      artikelMenge: {
        ...item,
        menge: values.quantity,
      },
    });

    this.corrections.set(new Map(updatedMap));
  }

  async save(): Promise<unknown> {
    const active = this.active();
    if (!active || !active.palette?.id) return;

    return this.#repository.save(
      active.palette.id,
      Array.from(this.corrections().values())
    );
  }
}
